import React, { useEffect, useMemo, useState } from 'react';
import styles from './ShareVia.module.scss';
import cx from 'classnames';
import ModalContent from '../../../Modal/ModalContent';
import ModalFooter from '../../../Modal/ModalFooter';
import VideoService from '../../../../service/VideoService';
import configs from '../../../../../../conf';
import SharingFooter from '../SharingFooter';
import { useTranslation } from 'react-i18next';
import useFetchFoldersList from '../../../../hooks/useFetchFoldersList';
import { useUserContext } from '../../../../context/UserContext';
import { FOLDER_USER_ROLE } from '../../../../constants/UserRoles';
import { approvalStatuses } from '../../../../features/Dashboard/components/VideosFilter/constants';
import Spinner from '../../../../../common/components/Spinner';
import Icon from '../../../Icon/Icon';
import { ToastNotification } from '../../../Toast/Toast';

const config = configs[process.env.APP_CONF];

const ShareVia = ({ videoId, videoPassword = '', isSkipAuthToken }) => {
  const [video, setVideo] = useState({});
  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);
  const [isCanChangeVideoPassword, setIsCanChangeVideoPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { allFolders } = useFetchFoldersList({});

  const { user, isApprovalFlowEnabled, isReviewerUser, logFirebaseEvent } = useUserContext();

  const isPreExistedStatus = useMemo(() => video?.approvalStatus === approvalStatuses.PRE_EXISTED.TYPE, [video]);

  const { t } = useTranslation();

  const getVideoDetails = async (videoPassword, tries = 0) => {
    try {
      setIsLoading(true);
      let videoDetails = {};
      if (videoPassword?.length === 0) {
        videoDetails = await VideoService.getVideoDetails(videoId, isSkipAuthToken);
      } else {
        videoDetails = await VideoService.getProtectedVideo(videoId, videoPassword);
      }

      if (videoDetails === 404 && tries <= 100) {
        console.log('404, probing again in 3s');
        setTimeout(() => {
          getVideoDetails(videoPassword, tries + 1);
        }, 3000);
        return;
      }

      videoDetails.shareUrl = `${config.domain}/engage/${videoId}`;

      setVideo(videoDetails);
    } catch (e) {
      console.log(e);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (!video.author || !user) {
      return;
    }

    if (isApprovalFlowEnabled && !isPreExistedStatus) {
      if (isReviewerUser) {
        setIsCanChangeVideoPassword(true);
      } else {
        setIsCanChangeVideoPassword(false);
      }

      return;
    }

    if (video.author.username === user.username) {
      setIsCanChangeVideoPassword(true);
    }

    if (video.author.username !== user.username && video.folder) {
      const currentFolder = allFolders.find(({ id }) => id === video.folder);

      const isFolderEditor = currentFolder?.users?.find(
        ({ username, folderRole }) => username === user.username && folderRole === FOLDER_USER_ROLE.EDITOR.TYPE
      );
      const isFolderOwner = user.username === currentFolder?.owner?.username;

      setIsCanChangeVideoPassword(isFolderOwner || isFolderEditor);
    }
  }, [video, user, allFolders, isApprovalFlowEnabled, isReviewerUser, isPreExistedStatus]);

  useMemo(async () => {
    if (videoId) {
      getVideoDetails(videoPassword);
    }
  }, [videoId]);

  const handleCopyLink = async () => {
    navigator.clipboard.writeText(video?.shareUrl);
    ToastNotification({
      message: t('general.toast.link_copied'),
      iconName: 'success',
      isMobileView: isMobileView,
    });

    logFirebaseEvent('copy_link_cl', { story_id: videoId });
  };

  const shareViaLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${video?.shareUrl}`);
    logFirebaseEvent('share_cl_linkedin', { story_id: videoId });
  };

  const shareViaFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${video?.shareUrl}`);
    logFirebaseEvent('share_cl_facebook', { story_id: videoId });
  };

  return (
    <>
      <ModalContent className={styles.content}>
        <div className={styles.socialNetworkContainer}>
          <div className={cx(styles.socialNetworkItem, { [styles.disabled]: isLoading })} onClick={shareViaLinkedIn}>
            <Icon src="shareViaLinkedIn" className={styles.socialIcons} />
            <div>LinkedIn</div>
          </div>
          <div className={cx(styles.socialNetworkItem, { [styles.disabled]: isLoading })} onClick={shareViaFacebook}>
            <Icon src="shareViaFacebook" className={styles.socialIcons} />
            <div>Facebook</div>
          </div>
          <div className={cx(styles.socialNetworkItem, { [styles.disabled]: isLoading })} onClick={handleCopyLink}>
            <Icon src="copy_link" className={styles.socialIcons} />
            <div>{t('general.video_action_button.copy_link')}</div>
          </div>
        </div>
      </ModalContent>
      {isLoading ? <Spinner className={styles.spinner} size="2px" /> : null}
      {!isLoading && !!video?.author?.username ? (
        <ModalFooter className={styles.footer}>
          <SharingFooter
            videoId={videoId}
            isCanChangeVideoPassword={isCanChangeVideoPassword}
            videoUrl={video?.shareUrl}
            isMobileView={isMobileView}
            isSkipAuthToken={isSkipAuthToken}
          />
        </ModalFooter>
      ) : null}
    </>
  );
};

export default ShareVia;
